import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef } from 'react';
import { FaList, FaUser } from 'react-icons/fa';
import { Col, Grid as BaseGrid, Row } from 'react-styled-flexboxgrid';
import Alert from 'reusecore/src/elements/Alert';
import Button from '../../../reusecore/src/elements/Button';
import ProActionLoader from '../../components/Loader/ProActionLoader';
import ProDescriptionLoader from '../../components/Loader/ProDescriptionLoader';
import ProHoursLoader from '../../components/Loader/ProHoursLoader';
import ProInfosLoader from '../../components/Loader/ProInfosLoader';
import ProLoader from '../../components/Loader/ProLoader';
import {
  Image,
  ImageWrapper,
} from '../../components/UserInfoBox/userInfoBox.style';
import { Counter } from '../../helpers/Counter';
import { db } from '../../helpers/firebaseHelper';
import useMediaQuery from '../../helpers/mediaQuery';
import UserPost from './UserPost';
import styled from 'styled-components';
import CoverImage from './CoverImage';
import Box from 'reusecore/src/elements/Box';
import AvatarInitials from '../../components/Avatar';
import { dateFormatAuthor } from '../../helpers/dateHelper';
import UserReviews from './UserReviews';
import colors from '../../theme/colors';
import { MdComment, MdPerson, MdVerified } from 'react-icons/md';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthProvider';
import Icon from 'react-icons-kit';
import { ACCOUNT_SETTING_PAGE } from '@core/ui/navigation/constant';
import { pencil } from 'react-icons-kit/iconic/pencil';
import { useRouter } from 'next/router';

const Grid = styled(BaseGrid)`
  padding-right: 15px;
  padding-left: 15px;
`;

const UserPage = props => {
  const { data: { author = {} } = {}, error, loading } = props;

  const router = useRouter();

  const { t } = useTranslation();

  const userPosts = useRef(null);
  const userReviewsRef = useRef(null);

  const isMobile = useMediaQuery('(max-width: 600px)');
  const isTablet = useMediaQuery('(max-width: 1023px)');

  const { isLoggedIn } = useAuth();
  const { state: { userId } = {} } = useUser();

  const {
    alternateName,
    description,
    image,
    imageProfil: avatar,
    certified,
    uid,
    createdAt,
  } = author;

  const canEdit = () => {
    return isLoggedIn && uid && uid === userId;
  };

  const Loader = () => (
    <>
      <Row>
        <Col xs={12} sm={8} md={5}>
          <ProLoader />
        </Col>
        <Col xs={12} sm={4} md={4}>
          <ProInfosLoader />
        </Col>
        <Col xs={12} sm={4} md={3}>
          <ProActionLoader />
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={6}>
          <ProDescriptionLoader />
        </Col>
        <Col xs={12} sm={12} md={1} style={{ height: 30 }} />
        <Col xs={12} sm={12} md={5}>
          <ProHoursLoader />
        </Col>
      </Row>
    </>
  );

  useEffect(() => {
    if (uid) {
      // Initialize the sharded counter.
      const visits = new Counter(
        db.collection('users').doc(uid),
        'stats.visits'
      );

      // Increment the field "visits" of the document
      visits.incrementBy(1);
    }
  }, [uid]);

  if (error) return <Alert>{`Error! ${error.message}`}</Alert>;

  return (
    <div>
      <CoverImage />
      <Grid>
        {loading ? (
          <Loader />
        ) : (
          <div
            style={{
              backgroundColor: '#F9F9F9',
              position: 'relative',
              borderTopLeftRadius: 15,
              borderTopRightRadius: 15,
              marginTop: -50,
              paddingTop: '2rem',
              zIndex: 1,
              paddingLeft: '2rem',
              paddingRight: '2rem',
            }}
          >
            <Row>
              <Col xs={12} sm={8} md={5}>
                <Row style={{ color: '#002F3F' }}>
                  <Col xs={4} style={{ padding: 0 }}>
                    {avatar != null && avatar != '' ? (
                      <ImageWrapper
                        imgWidth={112}
                        imgHeight={112}
                        imgRadius={85}
                        style={{ marginBottom: 10 }}
                      >
                        <Image
                          src={avatar}
                          alt="image"
                          width="112px"
                          height="112px"
                        />
                      </ImageWrapper>
                    ) : (
                      <>
                        <Box>
                          <AvatarInitials
                            customStyle={{
                              height: 112,
                              width: 112,
                              marginBottom: 10,
                            }}
                            customSpanStyle={{
                              fontSize: 35,
                            }}
                            user={author}
                          />
                        </Box>
                      </>
                    )}
                  </Col>
                  <Col xs={8} style={{ paddingTop: 7 }}>
                    <h2 style={{ display: 'flex', alignItems: 'center' }}>
                      {alternateName}
                      {certified && (
                        <MdVerified
                          size={20}
                          style={{ color: `${colors.primary}`, marginLeft: 5 }}
                        />
                      )}
                    </h2>
                    {certified && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 10,
                        }}
                      >
                        <MdVerified
                          size={20}
                          style={{ color: `${colors.primary}`, marginRight: 5 }}
                        />
                        <span>{t('user.certified')}</span>
                      </div>
                    )}
                    {createdAt && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 10,
                        }}
                      >
                        <MdPerson
                          size={20}
                          style={{ color: `${colors.primary}`, marginRight: 5 }}
                        />
                        <span>
                          {t('user.memberSince')} {dateFormatAuthor(createdAt)}
                        </span>
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
              <Col
                xs={12}
                sm={4}
                md={4}
                style={{ paddingTop: isMobile ? 20 : 49 }}
              ></Col>
              <Col
                xs={12}
                sm={4}
                md={3}
                style={{ paddingTop: isMobile || isTablet ? 42 : 0 }}
              >
                <Button
                  title={t('user.actions.ourAds')}
                  fontSize={12}
                  icon={
                    <FaList
                      size={15}
                      color="#fff"
                      style={{ marginRight: 10 }}
                    />
                  }
                  iconPosition="left"
                  className="primary"
                  bg="#00BCD4"
                  style={{
                    fontSize: 15,
                    marginBottom: 15,
                    width: '100%',
                    padding: 15,
                    fontWeight: 'bold',
                  }}
                  onClick={() =>
                    userPosts && userPosts.current
                      ? userPosts.current.scrollIntoView({
                          block: 'nearest',
                          inline: 'nearest',
                          behavior: 'smooth',
                        })
                      : null
                  }
                />
                <Button
                  title={t('user.actions.ourReviews')}
                  fontSize={12}
                  icon={
                    <MdComment
                      size={16}
                      color="#fff"
                      style={{ marginRight: 10 }}
                    />
                  }
                  iconPosition="left"
                  className="primary"
                  bg="#155876"
                  style={{
                    fontSize: 15,
                    marginBottom: 15,
                    width: '100%',
                    padding: 15,
                    fontWeight: 'bold',
                  }}
                  onClick={() =>
                    userReviewsRef && userReviewsRef.current
                      ? userReviewsRef.current.scrollIntoView({
                          block: 'nearest',
                          inline: 'nearest',
                          behavior: 'smooth',
                        })
                      : null
                  }
                />
              </Col>
            </Row>
            <Row style={{ paddingTop: 50 }}>
              {description && (
                <>
                  <Col
                    xs={12}
                    sm={12}
                    md={6}
                    style={{
                      padding: 30,
                      border: '1px solid #DDD',
                      borderRadius: 10,
                      position: 'relative',
                    }}
                  >
                    {canEdit() && (
                      <Button
                        style={{
                          padding: 15,
                          backgroundColor: 'transparent',
                          position: 'absolute',
                          right: 0,
                          top: 0,
                        }}
                        icon={
                          <Icon
                            title={t('update')}
                            icon={pencil}
                            size={18}
                            style={{
                              color: `${colors.primaryHover}`,
                            }}
                          />
                        }
                        onClick={() => router.push(`/${ACCOUNT_SETTING_PAGE}`)}
                      />
                    )}
                    <h3>{t('user.description.title')}</h3>
                    <p style={{ textAlign: 'justify', lineHeight: '25px' }}>
                      {description}
                    </p>
                  </Col>
                  <Col xs={12} sm={12} md={1} style={{ height: 30 }} />
                </>
              )}
            </Row>
            <Row ref={userPosts} style={{ paddingTop: 35 }}>
              <UserPost uid={uid} />
            </Row>
            <Row ref={userReviewsRef} style={{ paddingTop: 35 }}>
              <UserReviews uid={uid} />
            </Row>
          </div>
        )}
      </Grid>
    </div>
  );
};

export default UserPage;
